import { useState, useEffect, useCallback } from "react";

const ADMIN_PW = "Adm/in!Tulipe16%888*68865";

function generateOrderId() {
  return "HOMK" + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 5).toUpperCase();
}

// ── API 工具函数
async function api(method, path, body) {
  const res = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ════════════════════════════════════════════════════════
// ROOT APP
// ════════════════════════════════════════════════════════
export default function App() {
  const [session, setSession] = useState(() => {
    try { const s = sessionStorage.getItem("homk_session"); return s ? JSON.parse(s) : null; }
    catch { return null; }
  });
  const [view, setView] = useState(() => {
    try {
      const s = sessionStorage.getItem("homk_session");
      if (!s) return "login";
      const p = JSON.parse(s);
      return p.tier === "admin" ? "admin" : "store";
    } catch { return "login"; }
  });

  const [shoes, setShoes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pwConfig, setPwConfig] = useState({});
  const [coupons, setCoupons] = useState({});
  const [qrConfig, setQrConfig] = useState({ wechat: "", alipay: "" });
  const [dbReady, setDbReady] = useState(false);

  const [selectedShoe, setSelectedShoe] = useState(null);
  const [orderModal, setOrderModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [adminTab, setAdminTab] = useState("orders");
  const [editShoe, setEditShoe] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [shoesData, pwData, couponData, qrData] = await Promise.all([
        api("GET", "/api/shoes"),
        api("GET", "/api/config/passwords"),
        api("GET", "/api/config/coupons"),
        api("GET", "/api/config/qrcode"),
      ]);
      setShoes(shoesData);
      setPwConfig(pwData);
      setCoupons(couponData);
      setQrConfig(qrData);
      setDbReady(true);
    } catch (err) {
      console.error("Load error:", err);
      setDbReady(true);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const data = await api("GET", "/api/orders");
      setOrders(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    loadData();
    if (view === "admin") loadOrders();
  }, [loadData, loadOrders, view]);

  function login(pw) {
    const found = pwConfig[pw.trim()];
    if (found) {
      const s = { password: pw.trim(), ...found };
      setSession(s);
      sessionStorage.setItem("homk_session", JSON.stringify(s));
      const newView = found.tier === "admin" ? "admin" : "store";
      setView(newView);
      if (newView === "admin") loadOrders();
      return true;
    }
    return false;
  }

  function logout() {
    setSession(null);
    sessionStorage.removeItem("homk_session");
    setView("login");
    setSelectedShoe(null);
  }

  function getPrice(shoe) {
    if (!session) return shoe.price;
    return Math.round(shoe.price * session.discount);
  }

  // 特别密码：库存显示为0，无法下单
  function getDisplayShoe(shoe) {
    if (session?.tier === "preview") {
      const zeroStock = {};
      (shoe.sizes || []).forEach(s => { zeroStock[s] = 0; });
      return { ...shoe, stock: zeroStock, sold: true };
    }
    return shoe;
  }

  async function placeOrder(form) {
    const orderId = generateOrderId();
    const shoe = orderModal.shoe;
    const size = orderModal.size;
    const basePrice = getPrice(shoe);
    const finalPrice = form.couponDiscount ? basePrice - form.couponDiscount : basePrice;
    try {
      await api("POST", "/api/orders", {
        id: orderId, shoeId: shoe.id, shoeName: shoe.name, size,
        name: form.name, phone: form.phone, address: form.address, note: form.note || "",
        price: finalPrice, originalPrice: basePrice,
        couponCode: form.couponCode || "", couponDiscount: form.couponDiscount || 0,
        tier: session.label,
      });
      // 刷新鞋款库存
      const updated = await api("GET", "/api/shoes");
      setShoes(updated);
      setOrderModal(null);
      setPaymentModal({ orderId, price: finalPrice });
    } catch (err) { alert("下单失败：" + err.message); }
  }

  if (!dbReady) return <LoadingScreen />;
  if (view === "login") return <LoginPage onLogin={login} pwConfig={pwConfig} />;
  if (view === "admin") return (
    <AdminPanel shoes={shoes} orders={orders} pwConfig={pwConfig}
      coupons={coupons} qrConfig={qrConfig}
      onLogout={logout} adminTab={adminTab} setAdminTab={setAdminTab}
      editShoe={editShoe} setEditShoe={setEditShoe}
      onRefresh={() => { loadData(); loadOrders(); }}
    />
  );

  return (
    <div style={{ minHeight: "100vh", background: "#0e0d0c", color: "#e8e0d5" }}>
      {selectedShoe
        ? <ShoeDetail shoe={getDisplayShoe(selectedShoe)} session={session} getPrice={getPrice}
            onBack={() => setSelectedShoe(null)}
            onOrder={(size) => setOrderModal({ shoe: selectedShoe, size })} />
        : <Storefront shoes={shoes.map(getDisplayShoe)} session={session} getPrice={getPrice}
            onSelect={(shoe) => setSelectedShoe(shoes.find(s => s.id === shoe.id) || shoe)} onLogout={logout} />
      }
      {orderModal && (
        <OrderModal shoe={orderModal.shoe} size={orderModal.size}
          price={getPrice(orderModal.shoe)} coupons={coupons}
          onClose={() => setOrderModal(null)} onSubmit={placeOrder} />
      )}
      {paymentModal && (
        <PaymentModal orderId={paymentModal.orderId} price={paymentModal.price}
          qrConfig={qrConfig} onClose={() => setPaymentModal(null)} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════
// LOADING
// ════════════════════════════════════════════════════════
function LoadingScreen() {
  return (
    <div style={{ minHeight: "100vh", background: "#0e0d0c", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, letterSpacing: "0.4em", color: "#3a3530" }}>LOADING</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// LOGIN
// ════════════════════════════════════════════════════════
function LoginPage({ onLogin }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    const ok = onLogin(pw);
    if (!ok) { setError(true); setShake(true); setTimeout(() => setShake(false), 500); }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0e0d0c", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=Lato:wght@300;400&display=swap');
        .login-box { animation: fadeIn 1.2s ease forwards; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .shake { animation: shake 0.4s ease; }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(6px)} }
        .pw-input { background:transparent; border:none; border-bottom:1px solid #4a4540; color:#e8e0d5; font-family:'Lato',sans-serif; font-size:18px; letter-spacing:0.4em; text-align:center; padding:12px 0; width:100%; outline:none; transition:border-color 0.3s; }
        .pw-input:focus { border-bottom-color:#b8a898; }
        .pw-btn { background:transparent; border:1px solid #4a4540; color:#b8a898; font-family:'Lato',sans-serif; font-weight:300; letter-spacing:0.2em; font-size:12px; padding:12px 40px; cursor:pointer; transition:all 0.3s; margin-top:2rem; }
        .pw-btn:hover { border-color:#b8a898; color:#e8e0d5; }
      `}</style>
      <div className="login-box" style={{ textAlign: "center", maxWidth: 360, width: "100%" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 11, letterSpacing: "0.4em", color: "#6a635e", marginBottom: "2rem", textTransform: "uppercase" }}>Private Archive</div>
        <img src="https://i.ibb.co/FLtDBRYN/image2.png" alt="HOMK" style={{ width: 160, height: 160, borderRadius: "50%", marginBottom: "1.5rem", objectFit: "cover" }} />
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, lineHeight: 1.2, color: "#e8e0d5" }}>HOMK工装靴</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, lineHeight: 1.2, color: "#e8e0d5", marginBottom: "0.3rem" }}>粉丝福利馆</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 15, color: "#6a635e", marginBottom: "2.5rem" }}>粉丝特供 · 限量特价</div>
        <form onSubmit={handleSubmit} className={shake ? "shake" : ""}>
          <input className="pw-input" type="password" placeholder="输入访问密码"
            value={pw} onChange={e => { setPw(e.target.value); setError(false); }} autoFocus />
          {error && <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#8a6060", marginTop: "0.8rem", letterSpacing: "0.1em" }}>密码错误，请重试</div>}
          <div><button className="pw-btn" type="submit">进入</button></div>
        </form>
        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#3a3530", marginTop: "3rem", letterSpacing: "0.2em" }}>仅限受邀访客</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// STOREFRONT
// ════════════════════════════════════════════════════════
function Storefront({ shoes, session, getPrice, onSelect, onLogout }) {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "sold" ? shoes.filter(s => s.sold)
    : filter === "available" ? shoes.filter(s => !s.sold) : shoes;

  return (
    <div style={{ minHeight: "100vh", background: "#0e0d0c" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=Lato:wght@300;400&display=swap');
        .shoe-card { cursor:pointer; position:relative; overflow:hidden; transition:transform 0.4s ease; }
        .shoe-card:hover { transform:scale(1.02); }
        .shoe-card img { width:100%; height:300px; object-fit:cover; display:block; transition:transform 0.6s ease; filter:brightness(0.85); }
        .shoe-card:hover img { transform:scale(1.05); filter:brightness(0.95); }
        .filter-btn { background:transparent; border:none; color:#6a635e; font-family:'Lato',sans-serif; font-size:11px; letter-spacing:0.2em; cursor:pointer; padding:4px 0; transition:color 0.2s; }
        .filter-btn.active { color:#e8e0d5; border-bottom:1px solid #b8a898; }
        .filter-btn:hover { color:#c0b8b0; }
      `}</style>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.5rem 2.5rem", borderBottom: "1px solid #1e1c1a" }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, color: "#e8e0d5", letterSpacing: "0.05em", lineHeight: 1.3 }}>HOMK工装靴<br />粉丝福利馆</div>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.3em", color: "#4a4540", marginTop: 2 }}>PRIVATE ARCHIVE</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, letterSpacing: "0.15em", color: "#8a7d72", background: "#1a1815", padding: "6px 14px", borderRadius: 2 }}>
            {session.label}
            {session.discount < 1 && <span style={{ color: "#b8a898", marginLeft: 6 }}>-{Math.round((1 - session.discount) * 100)}%</span>}
          </div>
          <button onClick={onLogout} style={{ background: "transparent", border: "none", color: "#4a4540", fontFamily: "'Lato', sans-serif", fontSize: 11, letterSpacing: "0.15em", cursor: "pointer" }}>退出</button>
        </div>
      </header>
      <div style={{ padding: "2rem 2.5rem 1rem", display: "flex", gap: "2rem" }}>
        {[["all", "全部"], ["available", "有货"], ["sold", "售罄"]].map(([v, l]) => (
          <button key={v} className={`filter-btn ${filter === v ? "active" : ""}`} onClick={() => setFilter(v)}>{l}</button>
        ))}
      </div>
      <div style={{ padding: "1rem 2.5rem 4rem", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "2px" }}>
        {filtered.map(shoe => (
          <div key={shoe.id} className="shoe-card" onClick={() => onSelect(shoe)}>
            <div style={{ position: "relative" }}>
              <img src={shoe.images?.[0]?.trim()} alt={shoe.name} />
              {shoe.sold && <div style={{ position: "absolute", top: 16, left: 16, background: "#0e0d0c", color: "#8a7d72", fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.3em", padding: "4px 10px" }}>售罄</div>}
              {shoe.featured && !shoe.sold && <div style={{ position: "absolute", top: 16, left: 16, background: "#3a2e22", color: "#c8a87a", fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", padding: "4px 10px" }}>稀有皮料</div>}
            </div>
            <div style={{ padding: "1.2rem 1rem 1.5rem", background: "#121110" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 300, color: "#e8e0d5", marginBottom: 4 }}>{shoe.name}</div>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#6a635e", letterSpacing: "0.1em", marginBottom: "0.8rem" }}>{shoe.material}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 16, color: "#c8a87a" }}>¥{getPrice(shoe).toLocaleString()}</span>
                {shoe.originalPrice > 0 && <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#3a3530", textDecoration: "line-through" }}>¥{shoe.originalPrice.toLocaleString()}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// SHOE DETAIL
// ════════════════════════════════════════════════════════
function ShoeDetail({ shoe, session, getPrice, onBack, onOrder }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [selSize, setSelSize] = useState(null);
  const price = getPrice(shoe);

  return (
    <div style={{ minHeight: "100vh", background: "#0e0d0c" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=Lato:wght@300;400&display=swap');
        .size-btn { background:transparent; border:1px solid #2a2622; color:#8a7d72; font-family:'Lato',sans-serif; font-size:12px; padding:10px 16px; cursor:pointer; transition:all 0.2s; }
        .size-btn:hover:not(:disabled) { border-color:#6a5a4a; color:#c0b8b0; }
        .size-btn.sel { border-color:#b8a898; color:#e8e0d5; background:#1e1c1a; }
        .size-btn:disabled { opacity:0.25; cursor:not-allowed; text-decoration:line-through; }
        .order-btn { background:transparent; border:1px solid #b8a898; color:#e8e0d5; font-family:'Lato',sans-serif; font-size:12px; letter-spacing:0.25em; padding:14px 48px; cursor:pointer; transition:all 0.3s; }
        .order-btn:hover:not(:disabled) { background:#2a2420; }
        .order-btn:disabled { opacity:0.3; cursor:not-allowed; }
        .thumb { cursor:pointer; opacity:0.5; transition:opacity 0.2s; border:1px solid transparent; }
        .thumb.active { opacity:1; border-color:#b8a898; }
        .thumb:hover { opacity:0.8; }
        .detail-grid { display:grid; grid-template-columns:1fr 1fr; min-height:calc(100vh - 70px); }
        @media (max-width: 768px) { .detail-grid { grid-template-columns:1fr; } }
      `}</style>
      <header style={{ display: "flex", alignItems: "center", gap: "1.5rem", padding: "1.5rem 2.5rem", borderBottom: "1px solid #1e1c1a" }}>
        <button onClick={onBack} style={{ background: "transparent", border: "none", color: "#b8a898", fontFamily: "'Lato', sans-serif", fontSize: 22, letterSpacing: "0.1em", cursor: "pointer" }}>← 返回</button>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 14, color: "#4a4540" }}>{shoe.name}</div>
      </header>
      <div className="detail-grid">
        <div style={{ padding: "2rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0908", minHeight: 300 }}>
            <img src={shoe.images?.[imgIdx]?.trim()} alt={shoe.name} style={{ width: "100%", height: "auto", maxHeight: 600, objectFit: "contain", display: "block", filter: "brightness(0.95)" }} />
          </div>
          {shoe.images?.length > 1 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {shoe.images.map((img, i) => (
                <img key={i} src={img?.trim()} alt="" className={`thumb ${i === imgIdx ? "active" : ""}`} onClick={() => setImgIdx(i)} style={{ width: 72, height: 72, objectFit: "cover" }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: "3rem 2.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.35em", color: "#4a4540", marginBottom: 8 }}>ARCHIVE</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 38, fontWeight: 300, color: "#e8e0d5", margin: 0, lineHeight: 1.1 }}>{shoe.name}</h1>
          </div>
          <p style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: "#8a7d72", lineHeight: 1.8, margin: 0 }}>{shoe.description}</p>
          <div style={{ borderTop: "1px solid #1e1c1a", borderBottom: "1px solid #1e1c1a", padding: "1.2rem 0", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {[["皮料", shoe.material], ["工艺", shoe.craft]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, letterSpacing: "0.15em", color: "#4a4540" }}>{k}</span>
                <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#c0b8b0" }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, color: "#c8a87a" }}>¥{price.toLocaleString()}</span>
            {shoe.originalPrice > 0 && <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 14, color: "#3a3530", textDecoration: "line-through" }}>¥{shoe.originalPrice.toLocaleString()}</span>}
            {session.discount < 1 && <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#8a6a50" }}>{session.label}专享</span>}
          </div>
          <div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.25em", color: "#4a4540", marginBottom: 12 }}>选择尺码</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(shoe.sizes || []).map(s => {
                const inStock = (shoe.stock?.[s] || 0) > 0;
                return (
                  <button key={s} className={`size-btn ${selSize === s ? "sel" : ""}`}
                    disabled={!inStock} onClick={() => setSelSize(s)}>
                    {s}
                    {inStock && shoe.stock[s] === 1 && <span style={{ fontSize: 9, color: "#8a5a3a", marginLeft: 4 }}>仅1双</span>}
                  </button>
                );
              })}
            </div>
            {(shoe.sizes || []).every(s => !(shoe.stock?.[s] > 0)) && (
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#6a4040", marginTop: 12, letterSpacing: "0.15em" }}>全码售罄</div>
            )}
          </div>
          <button className="order-btn" disabled={!selSize || shoe.sold} onClick={() => onOrder(selSize)}>
            {shoe.sold ? "售罄" : "下单"}
          </button>
          {shoe.sold && <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#5a4a3a", letterSpacing: "0.15em" }}>此款已售罄 — 稀有皮料通常 24 小时内售出</div>}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ORDER MODAL
// ════════════════════════════════════════════════════════
function OrderModal({ shoe, size, price, coupons, onClose, onSubmit }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", note: "", couponCode: "", couponDiscount: 0 });
  const [couponInput, setCouponInput] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const finalPrice = price - (form.couponDiscount || 0);

  function applyCoupon() {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    const found = coupons[code];
    if (found) {
      setForm(f => ({ ...f, couponCode: code, couponDiscount: found.discount }));
      setCouponMsg({ ok: true, text: `优惠码有效，减 ¥${found.discount}` });
    } else {
      setForm(f => ({ ...f, couponCode: "", couponDiscount: 0 }));
      setCouponMsg({ ok: false, text: "优惠码无效" });
    }
  }

  async function handleSubmit() {
    setLoading(true);
    await onSubmit(form);
    setLoading(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "1rem" }}>
      <style>{`
        .order-input { width:100%; background:transparent; border:none; border-bottom:1px solid #2a2622; color:#e8e0d5; font-family:'Lato',sans-serif; font-size:14px; padding:10px 0; outline:none; box-sizing:border-box; transition:border-color 0.2s; }
        .order-input:focus { border-bottom-color:#b8a898; }
        .submit-btn { width:100%; background:transparent; border:1px solid #b8a898; color:#e8e0d5; font-family:'Lato',sans-serif; letter-spacing:0.25em; font-size:12px; padding:14px; cursor:pointer; transition:background 0.3s; }
        .submit-btn:hover:not(:disabled) { background:#2a2420; }
        .submit-btn:disabled { opacity:0.4; cursor:not-allowed; }
      `}</style>
      <div style={{ background: "#121110", maxWidth: 480, width: "100%", padding: "2.5rem", position: "relative", maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 20, right: 20, background: "transparent", border: "none", color: "#4a4540", fontSize: 20, cursor: "pointer" }}>×</button>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, color: "#e8e0d5", marginBottom: 4 }}>确认下单</div>
        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#6a635e", letterSpacing: "0.1em", marginBottom: "2rem" }}>
          {shoe.name} · {size}码 · <span style={{ color: "#c8a87a" }}>¥{price.toLocaleString()}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
          {[["name", "姓名", "您的姓名"], ["phone", "联系方式", "手机号码"], ["address", "收货地址", "省市区街道详细地址"], ["note", "备注", "特殊要求（选填）"]].map(([k, l, ph]) => (
            <div key={k}>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>{l}</div>
              {k === "address"
                ? <textarea className="order-input" placeholder={ph} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} rows={2} style={{ resize: "none" }} />
                : <input className="order-input" placeholder={ph} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />}
            </div>
          ))}
          <div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>优惠码（选填）</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="order-input" placeholder="输入优惠码" value={couponInput}
                onChange={e => { setCouponInput(e.target.value); setCouponMsg(null); setForm(f => ({ ...f, couponCode: "", couponDiscount: 0 })); }}
                onKeyDown={e => e.key === "Enter" && applyCoupon()} style={{ flex: 1 }} />
              <button onClick={applyCoupon} style={{ background: "transparent", border: "1px solid #4a4540", color: "#8a7d72", fontFamily: "'Lato', sans-serif", fontSize: 11, padding: "0 14px", cursor: "pointer", whiteSpace: "nowrap" }}>使用</button>
            </div>
            {couponMsg && <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, marginTop: 6, color: couponMsg.ok ? "#6a9a6a" : "#9a5a5a" }}>{couponMsg.text}</div>}
          </div>
        </div>
        <div style={{ borderTop: "1px solid #1e1c1a", marginTop: "1.5rem", paddingTop: "1.2rem", display: "flex", flexDirection: "column", gap: 6 }}>
          {form.couponDiscount > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#6a635e" }}>
                <span>商品价格</span><span>¥{price.toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#6a9a6a" }}>
                <span>优惠码减免</span><span>-¥{form.couponDiscount}</span>
              </div>
            </>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: "#c8a87a" }}>
            <span>实付金额</span><span>¥{finalPrice.toLocaleString()}</span>
          </div>
        </div>
        <button className="submit-btn" style={{ marginTop: "1.5rem" }}
          disabled={!form.name || !form.phone || !form.address || loading}
          onClick={handleSubmit}>
          {loading ? "提交中..." : "提交订单 — 弹出付款方式"}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// PAYMENT MODAL
// ════════════════════════════════════════════════════════
function PaymentModal({ orderId, price, qrConfig, onClose }) {
  const [tab, setTab] = useState("wechat");
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
      <style>{`
        .tab-btn { background:transparent; border:none; color:#6a635e; font-family:'Lato',sans-serif; font-size:12px; letter-spacing:0.15em; padding:8px 0; cursor:pointer; border-bottom:1px solid transparent; transition:all 0.2s; }
        .tab-btn.active { color:#e8e0d5; border-bottom-color:#b8a898; }
      `}</style>
      <div style={{ background: "#121110", maxWidth: 400, width: "100%", padding: "2.5rem", textAlign: "center", position: "relative" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300, color: "#e8e0d5", marginBottom: 4 }}>扫码付款</div>
        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 28, color: "#c8a87a", margin: "0.8rem 0" }}>¥{price?.toLocaleString()}</div>
        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#4a4540", letterSpacing: "0.2em", marginBottom: "1.5rem" }}>订单号 {orderId}</div>
        <div style={{ display: "flex", gap: "2rem", justifyContent: "center", marginBottom: "1.5rem", borderBottom: "1px solid #1e1c1a" }}>
          <button className={`tab-btn ${tab === "wechat" ? "active" : ""}`} onClick={() => setTab("wechat")}>微信支付</button>
          <button className={`tab-btn ${tab === "alipay" ? "active" : ""}`} onClick={() => setTab("alipay")}>支付宝</button>
        </div>
        {(tab === "wechat" ? qrConfig?.wechat : qrConfig?.alipay) ? (
          <div style={{ background: "#fff", display: "inline-block", padding: 12, marginBottom: "1.5rem" }}>
            <img src={tab === "wechat" ? qrConfig.wechat : qrConfig.alipay} alt="支付二维码" style={{ width: 180, height: 180, display: "block", objectFit: "contain" }} />
          </div>
        ) : (
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#6a635e", marginBottom: "1.5rem", padding: "2rem" }}>
            请在后台收款码设置中上传二维码
          </div>
        )}
        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#6a635e", lineHeight: 2, marginBottom: "1.5rem" }}>
          付款后请截图发送给我<br />
          <span style={{ color: "#4a4540" }}>付款备注：{orderId}</span>
        </div>
        <button onClick={onClose} style={{ background: "transparent", border: "1px solid #2a2622", color: "#8a7d72", fontFamily: "'Lato', sans-serif", fontSize: 11, letterSpacing: "0.2em", padding: "10px 32px", cursor: "pointer" }}>
          已付款，关闭
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ADMIN PANEL
// ════════════════════════════════════════════════════════
function AdminPanel({ shoes, orders, pwConfig, coupons, qrConfig, onLogout, adminTab, setAdminTab, editShoe, setEditShoe, onRefresh }) {

  async function saveShoe(shoe) {
    try {
      if (shoe.id) {
        await api("PUT", `/api/shoes/${shoe.id}`, shoe);
      } else {
        await api("POST", "/api/shoes", shoe);
      }
      setEditShoe(null);
      onRefresh();
    } catch (err) { alert("保存失败：" + err.message); }
  }

  async function deleteShoe(id) {
    if (!window.confirm("确定删除这个款式？")) return;
    await api("DELETE", `/api/shoes/${id}`);
    onRefresh();
  }

  async function toggleSold(shoe) {
    await api("PATCH", `/api/shoes/${shoe.id}/sold`, { sold: !shoe.sold });
    onRefresh();
  }

  async function togglePaid(order, markUnpaid) {
    if (markUnpaid) {
      if (!window.confirm("确定标记为未付款？将自动退回库存。")) return;
      await api("PATCH", `/api/orders/${order.id}/unpaid`, {});
    } else {
      await api("PATCH", `/api/orders/${order.id}/paid`, { paid: true });
    }
    onRefresh();
  }

  async function deleteOrder(id) {
    if (!window.confirm("确定删除此订单？")) return;
    await api("DELETE", `/api/orders/${id}`);
    onRefresh();
  }

  async function savePwConfig(newConfig) {
    await api("POST", "/api/config/passwords", newConfig);
    onRefresh();
  }

  async function saveCoupons(newCoupons) {
    await api("POST", "/api/config/coupons", newCoupons);
    onRefresh();
  }

  async function saveQrConfig(newQr) {
    await api("POST", "/api/config/qrcode", newQr);
    onRefresh();
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0e0d0c", color: "#e8e0d5" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400&family=Lato:wght@300;400&display=swap');
        .atab { background:transparent; border:none; color:#4a4540; font-family:'Lato',sans-serif; font-size:11px; letter-spacing:0.2em; padding:12px 0; cursor:pointer; border-bottom:1px solid transparent; transition:all 0.2s; white-space:nowrap; }
        .atab.active { color:#e8e0d5; border-bottom-color:#b8a898; }
        .atab:hover { color:#8a7d72; }
        .a-btn { background:transparent; border:1px solid #2a2622; color:#8a7d72; font-family:'Lato',sans-serif; font-size:10px; letter-spacing:0.15em; padding:5px 12px; cursor:pointer; transition:all 0.2s; white-space:nowrap; }
        .a-btn:hover { border-color:#6a5a4a; color:#c0b8b0; }
        .a-btn.danger:hover { border-color:#6a3030; color:#c08080; }
        .a-btn.green { color:#6a9a6a; }
        .a-btn.green:hover { border-color:#4a7a4a; color:#8ac08a; }
        .a-input { background:#1a1815; border:1px solid #2a2622; color:#e8e0d5; font-family:'Lato',sans-serif; font-size:13px; padding:8px 12px; outline:none; width:100%; box-sizing:border-box; }
        .a-input:focus { border-color:#6a5a4a; }
      `}</style>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.2rem 2.5rem", borderBottom: "1px solid #1e1c1a" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 300 }}>HOMK · 管理后台</div>
        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#3a3530", letterSpacing: "0.15em" }}>
            {orders.filter(o => !o.paid).length} 笔待付款
          </div>
          <button onClick={onRefresh} style={{ background: "transparent", border: "none", color: "#4a4540", fontFamily: "'Lato', sans-serif", fontSize: 11, cursor: "pointer" }}>刷新</button>
          <button onClick={onLogout} style={{ background: "transparent", border: "none", color: "#4a4540", fontFamily: "'Lato', sans-serif", fontSize: 11, letterSpacing: "0.15em", cursor: "pointer" }}>退出</button>
        </div>
      </header>
      <div style={{ display: "flex", gap: "2rem", padding: "0 2.5rem", borderBottom: "1px solid #1e1c1a", overflowX: "auto" }}>
        {[["orders", "订单管理"], ["shoes", "款式管理"], ["passwords", "密码管理"], ["coupons", "优惠码"], ["qrcode", "收款码"]].map(([k, l]) => (
          <button key={k} className={`atab ${adminTab === k ? "active" : ""}`} onClick={() => setAdminTab(k)}>{l}</button>
        ))}
      </div>
      <div style={{ padding: "2rem 2.5rem" }}>
        {adminTab === "orders" && <OrdersTab orders={orders} onTogglePaid={togglePaid} onDelete={deleteOrder} />}
        {adminTab === "shoes" && (
          <ShoesTab shoes={shoes} onDelete={deleteShoe} onToggleSold={toggleSold}
            onEdit={setEditShoe} editShoe={editShoe} onSave={saveShoe} onCancel={() => setEditShoe(null)}
            onAdd={() => setEditShoe({ name: "", material: "", craft: "", sizes: [], stock: {}, price: 0, originalPrice: 0, images: [], description: "", sold: false, featured: false })} />
        )}
        {adminTab === "passwords" && <PasswordsTab pwConfig={pwConfig} onSave={savePwConfig} />}
        {adminTab === "coupons" && <CouponsTab coupons={coupons} onSave={saveCoupons} />}
        {adminTab === "qrcode" && <QrCodeTab qrConfig={qrConfig} onSave={saveQrConfig} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ORDERS TAB
// ════════════════════════════════════════════════════════
function OrdersTab({ orders, onTogglePaid, onDelete }) {
  const [filterPaid, setFilterPaid] = useState("all");
  const [search, setSearch] = useState({ id: "", name: "", size: "", coupon: "" });

  const filtered = orders.filter(o => {
    if (filterPaid === "unpaid" && o.paid) return false;
    if (filterPaid === "paid" && !o.paid) return false;
    if (search.id && !o.id?.toLowerCase().includes(search.id.toLowerCase())) return false;
    if (search.name && !(o.name?.toLowerCase().includes(search.name.toLowerCase()) || o.address?.toLowerCase().includes(search.name.toLowerCase()) || o.phone?.includes(search.name))) return false;
    if (search.size && o.size !== search.size) return false;
    if (search.coupon && !o.couponCode?.toLowerCase().includes(search.coupon.toLowerCase())) return false;
    return true;
  });

  const hasSearch = search.id || search.name || search.size || search.coupon;

  if (orders.length === 0) return (
    <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: "#4a4540", textAlign: "center", padding: "4rem", letterSpacing: "0.1em" }}>暂无订单</div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: "1.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {[["all", `全部(${orders.length})`], ["unpaid", `待付款(${orders.filter(o => !o.paid).length})`], ["paid", `已付款(${orders.filter(o => o.paid).length})`]].map(([v, l]) => (
          <button key={v} className={`a-btn ${filterPaid === v ? "green" : ""}`} onClick={() => setFilterPaid(v)}>{l}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.6fr 1fr auto", gap: 8, marginBottom: "1.5rem", alignItems: "center" }}>
        {[["id", "搜索订单号"], ["name", "搜索收货人/地址/电话"], ["size", "搜索尺码"], ["coupon", "搜索优惠码"]].map(([k, ph]) => (
          <input key={k} className="a-input" placeholder={ph} value={search[k]}
            onChange={e => setSearch(s => ({ ...s, [k]: e.target.value }))}
            style={{ fontSize: 11, padding: "6px 10px" }} />
        ))}
        <button className="a-btn" onClick={() => setSearch({ id: "", name: "", size: "", coupon: "" })}
          style={{ opacity: hasSearch ? 1 : 0.3 }}>清空</button>
      </div>
      {filtered.length === 0 && <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#4a4540", padding: "2rem 0" }}>没有符合条件的订单</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr 1fr 0.6fr 0.6fr 0.8fr 1.2fr", gap: 12, padding: "0 12px 12px", fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.15em", color: "#4a4540" }}>
          {["订单号/时间", "收货人/地址", "鞋款/尺码", "金额", "等级", "状态", "操作"].map(h => <div key={h}>{h}</div>)}
        </div>
        {filtered.map(o => (
          <div key={o.id} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr 1fr 0.6fr 0.6fr 0.8fr 1.2fr", gap: 12, padding: "14px 12px", background: o.paid ? "#0c100e" : "#121110", borderLeft: `2px solid ${o.paid ? "#3a6a3a" : "#2a2622"}`, alignItems: "start" }}>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#6a635e" }}>
              {o.id}<br /><span style={{ color: "#3a3530" }}>{o.createdAtStr || "—"}</span>
            </div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#c0b8b0" }}>
              {o.name}<br />
              <span style={{ fontSize: 11, color: "#6a635e" }}>{o.phone}</span><br />
              <span style={{ fontSize: 10, color: "#4a4540", lineHeight: 1.6 }}>{o.address}</span>
              {o.note && <><br /><span style={{ fontSize: 10, color: "#5a5048" }}>备注: {o.note}</span></>}
              {o.couponCode && <><br /><span style={{ fontSize: 10, color: "#6a9a6a" }}>优惠码: {o.couponCode} (-¥{o.couponDiscount})</span></>}
            </div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#c0b8b0" }}>
              {o.shoeName}<br /><span style={{ fontSize: 14, color: "#e8e0d5" }}>{o.size} 码</span>
            </div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: "#c8a87a" }}>¥{o.price}</div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#8a7d72" }}>{o.tier}</div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: o.paid ? "#5a8a5a" : "#8a6a3a", letterSpacing: "0.05em" }}>
              {o.paid ? "✓ 已付款" : "待付款"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button className="a-btn green" onClick={() => onTogglePaid(o, false)}>标记付款</button>
              <button className="a-btn" style={{ color: "#c8a87a", borderColor: "#6a5a3a" }} onClick={() => onTogglePaid(o, true)}>标记未付款</button>
              <button className="a-btn danger" onClick={() => onDelete(o.id)}>删除</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// SHOES TAB
// ════════════════════════════════════════════════════════
function ShoesTab({ shoes, onDelete, onToggleSold, onEdit, onAdd, editShoe, onSave, onCancel }) {
  const [localShoe, setLocalShoe] = useState(null);
  const [newSize, setNewSize] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocalShoe(editShoe ? { ...editShoe } : null); setNewSize(""); }, [editShoe]);

  function updateField(k, v) { setLocalShoe(s => ({ ...s, [k]: v })); }

  function addSize() {
    const s = newSize.trim();
    if (!s || localShoe.sizes.includes(s)) return;
    setLocalShoe(sh => ({
      ...sh,
      sizes: [...sh.sizes, s].sort((a, b) => Number(a) - Number(b)),
      stock: { ...sh.stock, [s]: 0 },
    }));
    setNewSize("");
  }

  function removeSize(s) {
    setLocalShoe(sh => {
      const sizes = sh.sizes.filter(x => x !== s);
      const stock = { ...sh.stock };
      delete stock[s];
      return { ...sh, sizes, stock };
    });
  }

  function updateStock(s, v) {
    setLocalShoe(sh => ({ ...sh, stock: { ...sh.stock, [s]: Math.max(0, Number(v) || 0) } }));
  }

  async function handleSave() {
    setSaving(true);
    await onSave(localShoe);
    setSaving(false);
  }

  if (localShoe) return (
    <div style={{ maxWidth: 600 }}>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 300, marginBottom: "2rem" }}>
        {localShoe.id ? "编辑款式" : "添加款式"}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {[["name", "款式名称"], ["material", "皮料"], ["craft", "工艺"], ["description", "简介"]].map(([k, l]) => (
          <div key={k}>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>{l}</div>
            {k === "description"
              ? <textarea className="a-input" rows={3} style={{ resize: "vertical" }} value={localShoe[k] || ""} onChange={e => updateField(k, e.target.value)} />
              : <input className="a-input" value={localShoe[k] || ""} onChange={e => updateField(k, e.target.value)} />}
          </div>
        ))}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[["price", "特价(¥)"], ["originalPrice", "原价(¥)"]].map(([k, l]) => (
            <div key={k}>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>{l}</div>
              <input className="a-input" type="number" value={localShoe[k] || ""} onChange={e => updateField(k, Number(e.target.value))} />
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 10 }}>尺码与库存</div>
          {localShoe.sizes.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 32px", gap: 8, fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#4a4540", paddingBottom: 6, borderBottom: "1px solid #1e1c1a" }}>
                <span>尺码</span><span>库存数量</span><span></span>
              </div>
              {localShoe.sizes.map(s => (
                <div key={s} style={{ display: "grid", gridTemplateColumns: "80px 1fr 32px", gap: 8, alignItems: "center" }}>
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 14, color: "#e8e0d5", padding: "6px 0" }}>{s} 码</div>
                  <input className="a-input" type="number" min={0} value={localShoe.stock[s] ?? 0}
                    onChange={e => updateStock(s, e.target.value)} style={{ padding: "6px 10px" }} />
                  <button onClick={() => removeSize(s)} style={{ background: "transparent", border: "none", color: "#6a3a3a", fontSize: 16, cursor: "pointer", padding: 0 }}>×</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input className="a-input" placeholder="输入尺码，如 42" value={newSize}
              onChange={e => setNewSize(e.target.value)} onKeyDown={e => e.key === "Enter" && addSize()}
              style={{ maxWidth: 160 }} />
            <button className="a-btn" onClick={addSize}>+ 添加尺码</button>
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>图片链接（每行一个）</div>
          <textarea className="a-input" rows={3} value={(localShoe.images || []).join("\n")}
            onChange={e => updateField("images", e.target.value.split("\n").map(s => s.trim()).filter(Boolean))} />
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          {[["featured", "稀有皮料标签"], ["sold", "标记售罄"]].map(([k, l]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#8a7d72", cursor: "pointer" }}>
              <input type="checkbox" checked={localShoe[k] || false} onChange={e => updateField(k, e.target.checked)} />
              {l}
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: "1rem" }}>
          <button className="a-btn" style={{ color: "#c8a87a", borderColor: "#6a5a3a" }} onClick={handleSave} disabled={saving}>
            {saving ? "保存中..." : "保存"}
          </button>
          <button className="a-btn" onClick={onCancel}>取消</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <button className="a-btn" style={{ marginBottom: "1.5rem", color: "#c8a87a", borderColor: "#6a5a3a" }} onClick={onAdd}>+ 添加新款式</button>
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {shoes.map(s => (
          <div key={s.id} style={{ display: "grid", gridTemplateColumns: "60px 1fr 100px 80px auto", gap: 16, padding: "12px", background: "#121110", alignItems: "center" }}>
            {s.images?.[0] ? <img src={s.images[0]?.trim()} alt="" style={{ width: 60, height: 60, objectFit: "cover" }} /> : <div style={{ width: 60, height: 60, background: "#1a1815" }} />}
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: "#e8e0d5" }}>{s.name}</div>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#6a635e" }}>{s.material}</div>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#3a3530", marginTop: 2 }}>
                {(s.sizes || []).map(sz => `${sz}(${s.stock?.[sz] ?? 0})`).join(" · ")}
              </div>
            </div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: "#c8a87a" }}>¥{s.price?.toLocaleString()}</div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: s.sold ? "#8a5050" : "#5a8a5a" }}>
              {s.sold ? "售罄" : "在售"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="a-btn" onClick={() => onEdit(s)}>编辑</button>
              <button className="a-btn" onClick={() => onToggleSold(s)}>{s.sold ? "恢复在售" : "标记售罄"}</button>
              <button className="a-btn danger" onClick={() => onDelete(s.id)}>删除</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// PASSWORDS TAB
// ════════════════════════════════════════════════════════
function PasswordsTab({ pwConfig, onSave }) {
  const [local, setLocal] = useState({ ...pwConfig });
  const [newPw, setNewPw] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDiscount, setNewDiscount] = useState(100);
  const [newTier, setNewTier] = useState("fan");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal({ ...pwConfig }); }, [pwConfig]);

  async function addPw() {
    if (!newPw.trim()) return;
    const updated = { ...local, [newPw.trim()]: { tier: newTier, label: newLabel || newTier, discount: newDiscount / 100 } };
    setSaving(true);
    await onSave(updated);
    setSaving(false);
    setNewPw(""); setNewLabel(""); setNewDiscount(100);
  }

  async function removePw(pw) {
    if (pw === ADMIN_PW) return;
    const updated = { ...local };
    delete updated[pw];
    setSaving(true);
    await onSave(updated);
    setSaving(false);
  }

  const tiers = { fan: "普通粉丝", vip: "老客户", svip: "Super VIP", preview: "特别预览（只看价格）" };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 300, marginBottom: "2rem" }}>密码与等级管理</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: "2rem" }}>
        {Object.entries(local).map(([pw, cfg]) => (
          <div key={pw} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.6fr auto", gap: 12, padding: "12px", background: "#121110", alignItems: "center" }}>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: "#e8e0d5", wordBreak: "break-all" }}>{pw}</div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#8a7d72" }}>{cfg.label}</div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: cfg.discount < 1 ? "#c8a87a" : "#4a4540" }}>
              {cfg.discount < 1 ? `-${Math.round((1 - cfg.discount) * 100)}%` : "原价"}
            </div>
            {pw !== ADMIN_PW ? <button className="a-btn danger" onClick={() => removePw(pw)}>删除</button>
              : <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#3a3530" }}>系统</span>}
          </div>
        ))}
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 300, marginBottom: "1rem", color: "#8a7d72" }}>添加密码</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {[["密码", newPw, setNewPw, "例：FRIEND123"], ["显示名称", newLabel, setNewLabel, "老客户"]].map(([l, v, sv, ph]) => (
          <div key={l}>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>{l}</div>
            <input className="a-input" placeholder={ph} value={v} onChange={e => sv(e.target.value)} />
          </div>
        ))}
        <div>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>折扣 (%)</div>
          <input className="a-input" type="number" min={50} max={100} value={newDiscount} onChange={e => setNewDiscount(Number(e.target.value))} />
        </div>
        <div>
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>等级</div>
          <select className="a-input" value={newTier} onChange={e => setNewTier(e.target.value)}>
            {Object.entries(tiers).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <button className="a-btn" style={{ color: "#c8a87a", borderColor: "#6a5a3a" }} onClick={addPw} disabled={saving}>
        {saving ? "保存中..." : "添加密码"}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// COUPONS TAB
// ════════════════════════════════════════════════════════
function CouponsTab({ coupons, onSave }) {
  const [local, setLocal] = useState({ ...coupons });
  const [newCode, setNewCode] = useState("");
  const [newDiscount, setNewDiscount] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal({ ...coupons }); }, [coupons]);

  async function addCoupon() {
    if (!newCode.trim() || !newDiscount) return;
    const updated = { ...local, [newCode.trim().toUpperCase()]: { discount: Number(newDiscount), desc: newDesc || "" } };
    setSaving(true);
    await onSave(updated);
    setSaving(false);
    setNewCode(""); setNewDiscount(""); setNewDesc("");
  }

  async function removeCoupon(code) {
    const updated = { ...local };
    delete updated[code];
    setSaving(true);
    await onSave(updated);
    setSaving(false);
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 300, marginBottom: "0.5rem" }}>优惠码管理</div>
      <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#4a4540", marginBottom: "2rem" }}>客人下单时输入优惠码，自动减去对应金额</div>
      {Object.keys(local).length === 0 && <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#3a3530", marginBottom: "1.5rem" }}>暂无优惠码</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: "2rem" }}>
        {Object.entries(local).map(([code, cfg]) => (
          <div key={code} style={{ display: "grid", gridTemplateColumns: "1fr 0.6fr 1fr auto", gap: 12, padding: "12px", background: "#121110", alignItems: "center" }}>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 14, color: "#e8e0d5", letterSpacing: "0.1em" }}>{code}</div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: "#c8a87a" }}>-¥{cfg.discount}</div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#6a635e" }}>{cfg.desc || "—"}</div>
            <button className="a-btn danger" onClick={() => removeCoupon(code)}>删除</button>
          </div>
        ))}
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 300, marginBottom: "1rem", color: "#8a7d72" }}>添加优惠码</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
        {[["优惠码", newCode, setNewCode, "例：HOMK100"], ["减免金额(¥)", newDiscount, setNewDiscount, "例：100"], ["备注（选填）", newDesc, setNewDesc, "例：老客专享"]].map(([l, v, sv, ph]) => (
          <div key={l}>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>{l}</div>
            <input className="a-input" placeholder={ph} value={v} onChange={e => sv(e.target.value)} />
          </div>
        ))}
      </div>
      <button className="a-btn" style={{ color: "#c8a87a", borderColor: "#6a5a3a" }} onClick={addCoupon} disabled={saving}>
        {saving ? "保存中..." : "添加优惠码"}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// QR CODE TAB
// ════════════════════════════════════════════════════════
function QrCodeTab({ qrConfig, onSave }) {
  const [wechat, setWechat] = useState(qrConfig?.wechat || "");
  const [alipay, setAlipay] = useState(qrConfig?.alipay || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setWechat(qrConfig?.wechat || ""); setAlipay(qrConfig?.alipay || ""); }, [qrConfig]);

  async function handleSave() {
    setSaving(true);
    await onSave({ wechat, alipay });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 300, marginBottom: "0.5rem" }}>收款二维码设置</div>
      <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 11, color: "#4a4540", marginBottom: "2rem" }}>上传收款码图片到图床，把图片直链粘贴到下面</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {[["微信收款码", wechat, setWechat], ["支付宝收款码", alipay, setAlipay]].map(([label, val, setter]) => (
          <div key={label}>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#4a4540", marginBottom: 6 }}>{label}</div>
            <input className="a-input" placeholder="https://..." value={val} onChange={e => setter(e.target.value)} />
            {val && (
              <div style={{ marginTop: 12, background: "#fff", display: "inline-block", padding: 8 }}>
                <img src={val} alt={label} style={{ width: 120, height: 120, objectFit: "contain", display: "block" }} onError={e => { e.target.style.display = "none"; }} />
              </div>
            )}
          </div>
        ))}
      </div>
      <button className="a-btn" style={{ marginTop: "2rem", color: saved ? "#6a9a6a" : "#c8a87a", borderColor: saved ? "#4a7a4a" : "#6a5a3a" }} onClick={handleSave} disabled={saving}>
        {saving ? "保存中..." : saved ? "✓ 已保存" : "保存二维码"}
      </button>
    </div>
  );
}
